import React, { useState, useEffect, useCallback, useRef } from 'react';
import { C } from '../constants/theme';
import { Table } from '../components/Table';
import { Pagination } from '../components/Pagination';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { Modal } from '../components/Modal';
import { FormField, inputStyle } from '../components/FormField';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { formatDate, formatTimestamp } from '../utils/dateUtils';
import { getContentProvider, addOrUpdate } from '../utils/ContentProviderService';
import apiClient from '../utils/apiClient';
import { getCategories } from '../utils/CategoryService';
import { Pencil } from 'lucide-react';
import { CONTENT_CATEGORIES } from '../constants/options';
import { uploadFile } from '../utils/Uploadservice';

const cardStyle = {
    background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
    boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden',
};

const INIT_FORM = {
    name: '', providerLogo: '', category: '', contentCategory: '',
    feedType: '', feedLink: '', isGet: true, feedLimit: '', status: true,
    rootArrayKey: '',
};

const INIT_FIELD_MAPPING = { title: '', redirectLink: '', thumbnailUrl: '', description: '', postedDate: '' };

export function ContentProvider() {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(INIT_FORM);
    const [saving, setSaving] = useState(false);
    const [filters, setFilters] = useState([]);
    const [sortingColumn, setSortingColumn] = useState('id');
    const [direction, setDirection] = useState('ASC');
    const [testLoading, setTestLoading] = useState(false);
    const [testResults, setTestResults] = useState([]);
    const [testError, setTestError] = useState('');
    const [showTest, setShowTest] = useState(false);
    const [categories, setCategories] = useState([]);
    const [fullResponse, setFullResponse] = useState(null);
    const [availableKeys, setAvailableKeys] = useState([]);
    const [fieldMapping, setFieldMapping] = useState(INIT_FIELD_MAPPING);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getContentProvider({ pageNumber: page, pageSize, sortingColumn, direction, filters });
            const obj = res.responseObject;
            setData(obj.content);
            setTotal(obj.totalElements);
            setTotalPages(obj.totalPages);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, sortingColumn, direction, filters]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await getCategories({ pageNumber: 0, pageSize: 100, sortingColumn: 'id', direction: 'ASC', filters: [] });
            const obj = res?.responseObject;
            setCategories(obj?.content || []);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    const openAdd = () => {
        setForm(INIT_FORM);
        setFieldMapping(INIT_FIELD_MAPPING);
        setTestResults([]); setTestError(''); setShowTest(false);
        setFullResponse(null); setAvailableKeys([]);
        setModal('add');
    };

    const openEdit = (row) => {
        setForm({
            id: row.id,
            name: row.name,
            providerLogo: row.providerLogo,
            categoryId: row.categoryId,
            category: row.category,
            contentCategory: row.contentCategory,
            feedType: row.feedType,
            feedLink: row.feedLink,
            isGet: row.isGet,
            feedLimit: row.feedLimit,
            status: row.status,
            rootArrayKey: row.rootArrayKey || '',
        });

        // Deserialize jsonMappings array → fieldMapping object
        const mapping = { ...INIT_FIELD_MAPPING };
        (row.jsonMappings || []).forEach(({ dtoField, jsonKey }) => {
            if (dtoField in mapping) mapping[dtoField] = jsonKey;
        });
        setFieldMapping(mapping);

        setTestResults([]); setTestError(''); setShowTest(false);
        setFullResponse(null); setAvailableKeys([]);
        setModal(row);
    };

    const handleTestFeed = async () => {
        if (!form.feedLink) return;

        setTestLoading(true);
        setTestError('');
        setTestResults([]);
        setFullResponse(null);
        setAvailableKeys([]);
        setShowTest(true);

        try {
            if (form.feedType === 'JSON') {
                const response = await fetch(form.feedLink);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const result = await response.json();
                setFullResponse(result);

                let items = [];
                let detectedKey = '';

                if (Array.isArray(result)) {
                    items = result;
                    detectedKey = 'root';
                } else if (typeof result === 'object' && result !== null) {
                    const possibleKeys = ['data', 'items', 'results', 'content', 'games', 'list', 'feeds', 'articles', 'posts', 'sections'];
                    for (const key of possibleKeys) {
                        if (Array.isArray(result[key])) {
                            items = result[key];
                            detectedKey = key;
                            break;
                        }
                    }
                }

                if (items.length > 0) {
                    setTestResults(items.slice(0, 5));
                    setAvailableKeys(Object.keys(items[0]));
                    setForm(f => ({ ...f, rootArrayKey: detectedKey }));
                } else {
                    setTestError('Could not find array in response. Please select array key manually.');
                }
            } else {
                const res = await apiClient.get('/minusone-service/content/fetchRss', {
                    params: { url: form.feedLink, pageSize: 5 }
                });
                const result = res.data?.responseObject;
                if (result?.length > 0) setTestResults(result);
                else setTestError('No data found for this URL.');
            }
        } catch (e) {
            console.error(e);
            setTestError('Failed to fetch feed.');
        } finally {
            setTestLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {

            const jsonMappings = Object.entries(fieldMapping)
                .filter(([, jsonKey]) => jsonKey)
                .map(([dtoField, jsonKey]) => ({ dtoField, jsonKey }));

            const payload = {
                ...form,
                jsonMappings: form.feedType === 'JSON' ? jsonMappings : [],
            };

            await addOrUpdate(payload);
            setModal(null);
            fetchData();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };
    const UploadImageField = ({ value, onChange }) => {
        const inputRef = useRef(null);
        const [uploading, setUploading] = useState(false);
        const [error, setError] = useState(null);

        const handleFile = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            // Optional: Validate image
            if (!file.type.startsWith('image/')) {
                setError('Please select an image file');
                return;
            }

            setUploading(true);
            setError(null);

            try {
                const url = await uploadFile({ file });
                onChange(url);
            } catch (err) {
                setError('Upload failed. Please try again.');
                console.error(err);
            } finally {
                setUploading(false);
                e.target.value = ''; // Reset input
            }
        };

        return (
            <div style={{ marginTop: 6 }}>
                <input
                    type="file"
                    ref={inputRef}
                    accept="image/*"
                    onChange={handleFile}
                    style={{ display: 'none' }}
                />
                <Button
                    variant="outline"
                    small
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                >
                    {uploading ? 'Uploading...' : '📤 Upload Logo'}
                </Button>

                {error && <div style={{ color: '#c62828', fontSize: 12, marginTop: 4 }}>{error}</div>}
            </div>
        );
    };
    const columns = [
        {
            key: 'providerLogo', label: 'Logo',
            render: (val) => val
                ? <img src={val} alt="logo" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                : '—'
        },
        { key: 'name', label: 'Provider Name' },
        { key: 'category', label: 'Category', wrap: true },
        { key: 'contentCategory', label: 'Content Category', wrap: true },
        { key: 'feedType', label: 'Feed Type' },
        { key: 'feedLink', label: 'Feed Link', wrap: true },
        {
            key: 'isGet', label: 'Request',
            render: (val) => (
                <span style={{
                    display: 'inline-block', padding: '1px 8px', borderRadius: 10,
                    fontSize: 11, fontWeight: 600,
                    background: val ? '#E3F2FD' : '#FFF3E0',
                    color: val ? '#1565C0' : '#E65100',
                }}>
                    {val ? 'GET' : 'POST'}
                </span>
            )
        },
        { key: 'feedLimit', label: 'Feed Limit' },
        { key: 'status', label: 'Status', render: (v) => <Badge status={v ? 1 : 0} /> },
        { key: 'updatedAt', label: 'Updated At', render: (val) => formatDate(val) },
        { key: 'createdAt', label: 'Created At', render: (val) => formatDate(val) },
        { key: 'createdByName', label: 'Created By' },
        { key: 'updatedByName', label: 'Updated By' },
    ];

    return (
        <div>
            <PageHeader title="Content Provider" count={total} onAdd={openAdd} />
            <div style={cardStyle}>
                <div style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
                    <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(0); }} placeholder="Search provider…" />
                </div>
                {loading ? <LoadingSpinner /> : (
                    <Table columns={columns} data={data} actions={(row) => [
                        <Button key="e" small variant="outline" onClick={() => openEdit(row)}>
                            <Pencil size={13} strokeWidth={2} />
                        </Button>
                    ]} />
                )}
                <Pagination
                    page={page} totalPages={totalPages} totalElements={total} pageSize={pageSize}
                    onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
                />
            </div>

            {modal && (
                <Modal
                    title={modal === 'add' ? 'Add Content Provider' : 'Edit Content Provider'}
                    onClose={() => setModal(null)}
                    wide
                    style={{ width: showTest ? 900 : 520, maxWidth: '96vw' }}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: showTest ? 'minmax(360px, 1fr) minmax(380px, 1.2fr)' : '1fr', gap: 24 }}>

                        {/* LEFT — Form */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>

                            <FormField label="Provider Name" required>
                                <input style={inputStyle} value={form.name || ''} autoFocus
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="e.g. BBC News" />
                            </FormField>

                            <FormField label="Logo URL">
                                <div style={{ marginBottom: 8 }}>
                                    <UploadImageField
                                        value={form.providerLogo}
                                        onChange={(url) => setForm((f) => ({ ...f, providerLogo: url }))}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <input style={{ ...inputStyle, flex: 1 }} value={form.providerLogo || ''}
                                        onChange={(e) => setForm((f) => ({ ...f, providerLogo: e.target.value }))}
                                        placeholder="https://example.com/logo.png" />
                                    {form.providerLogo && (
                                        <img src={form.providerLogo} alt="preview"
                                            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1px solid #E5E7EB', flexShrink: 0 }}
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    )}
                                </div>
                            </FormField>

                            <FormField label="Category">
                                <select
                                    style={inputStyle}
                                    value={form.categoryId || ''}
                                    onChange={(e) => {
                                        const catId = Number(e.target.value);
                                        const selected = categories.find((cat) => cat.id === catId);
                                        setForm((f) => ({
                                            ...f,
                                            categoryId: catId,
                                            category: selected?.categoryName || ''
                                        }));
                                    }}
                                >
                                    <option value="">-- Select Category --</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.categoryName}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            <FormField label="Content Category">
                                <select style={inputStyle} value={form.contentCategory || ''}
                                    onChange={(e) => setForm((f) => ({ ...f, contentCategory: e.target.value }))}>
                                    <option value="">-- Select Content Category --</option>
                                    {CONTENT_CATEGORIES.map((val) => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </FormField>

                            <FormField label="Feed Type" required>
                                <select style={inputStyle} value={form.feedType || ''}
                                    onChange={(e) => setForm((f) => ({ ...f, feedType: e.target.value }))}>
                                    <option value="">-- Select Feed Type --</option>
                                    <option value="JSON">JSON</option>
                                    <option value="RSS">RSS</option>
                                </select>
                            </FormField>

                            <FormField label="Feed Limit">
                                <input style={inputStyle} type="number" value={form.feedLimit || ''}
                                    onChange={(e) => setForm((f) => ({ ...f, feedLimit: e.target.value }))}
                                    placeholder="e.g. 10" />
                            </FormField>

                            <FormField label="Request Type">
                                <select style={inputStyle} value={form.isGet}
                                    onChange={(e) => setForm((f) => ({ ...f, isGet: e.target.value === 'true' }))}>
                                    <option value={true}>GET</option>
                                    <option value={false}>POST</option>
                                </select>
                            </FormField>

                            <FormField label="Status" required>
                                <select style={inputStyle} value={form.status}
                                    onChange={(e) => setForm((f) => ({ ...f, status: Number(e.target.value) }))}>
                                    <option value="">-- Select Status --</option>
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </select>
                            </FormField>

                            <div style={{ gridColumn: '1 / -1' }}>
                                <FormField label="Feed Link" required>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input style={{ ...inputStyle, flex: 1 }} value={form.feedLink || ''}
                                            onChange={(e) => setForm((f) => ({ ...f, feedLink: e.target.value }))}
                                            placeholder="https://example.com/feed.rss" />
                                        <Button variant="outline" disabled={!form.feedLink || testLoading}
                                            onClick={handleTestFeed}>
                                            {testLoading ? '⏳' : '🧪'} Test Feed
                                        </Button>
                                    </div>
                                </FormField>
                            </div>

                        </div>

                        {/* RIGHT — Test Results */}
                        {showTest && (
                            <div style={{
                                borderLeft: `1px solid ${C.border}`, paddingLeft: 20,
                                display: 'flex', flexDirection: 'column', gap: 10,
                            }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: C.text, marginBottom: 4 }}>
                                    🧪 Feed Preview
                                </div>

                                {testLoading && <LoadingSpinner />}

                                {testError && (
                                    <div style={{ color: '#c62828', fontSize: 13 }}>{testError}</div>
                                )}

                                {/* RSS Preview */}
                                {!testLoading && form.feedType !== 'JSON' && testResults.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 420, overflowY: 'auto' }}>
                                        {testResults.map((item, i) => (
                                            <div key={i} style={{
                                                border: `1px solid ${C.border}`, borderRadius: 10,
                                                padding: 12, background: '#FAFAFA',
                                                display: 'flex', gap: 12,
                                            }}>
                                                {item.thumbnailUrl && (
                                                    <img src={item.thumbnailUrl} alt="thumb"
                                                        style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                                                        onError={(e) => e.target.style.display = 'none'}
                                                    />
                                                )}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#111' }}>
                                                        {item.title || '—'}
                                                    </div>
                                                    <div style={{
                                                        fontSize: 11, color: '#6B7280', marginBottom: 6,
                                                        overflow: 'hidden', textOverflow: 'ellipsis',
                                                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                                                    }}>
                                                        {item.description || '—'}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#9CA3AF', flexWrap: 'wrap' }}>
                                                        {item.postedDate && <span>📅 {formatTimestamp(item.postedDate)}</span>}
                                                        {item.redirectLink && (
                                                            <a href={item.redirectLink} target="_blank" rel="noreferrer"
                                                                style={{ color: '#6C3FC5', textDecoration: 'none' }}>
                                                                🔗 Read More
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* JSON Field Mapping */}
                                {!testLoading && form.feedType === 'JSON' && availableKeys.length > 0 && (
                                    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, background: '#F9FAFB' }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: C.text }}>
                                            🔗 Map JSON Keys to Fields
                                        </div>

                                        {/* rootArrayKey indicator */}
                                        {form.rootArrayKey && (
                                            <div style={{
                                                marginBottom: 12, fontSize: 12, color: '#374151',
                                                background: '#EEF2FF', border: '1px solid #C7D2FE',
                                                borderRadius: 6, padding: '5px 10px', display: 'inline-block'
                                            }}>
                                                📦 Array key detected: <strong>{form.rootArrayKey}</strong>
                                            </div>
                                        )}

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                            {[
                                                { label: 'Title', key: 'title', icon: '📝' },
                                                { label: 'Redirect Link', key: 'redirectLink', icon: '🔗' },
                                                { label: 'Thumbnail URL', key: 'thumbnailUrl', icon: '🖼️' },
                                                { label: 'Description', key: 'description', icon: '📄' },
                                                { label: 'Posted Date', key: 'postedDate', icon: '📄' },
                                            ].map(({ label, key, icon }) => (
                                                <div key={key}>
                                                    <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
                                                        {icon} {label}
                                                    </div>
                                                    <select
                                                        style={inputStyle}
                                                        value={fieldMapping[key] || ''}
                                                        onChange={(e) => setFieldMapping(prev => ({ ...prev, [key]: e.target.value }))}
                                                    >
                                                        <option value="">-- Select Key --</option>
                                                        {availableKeys.map(k => (
                                                            <option key={k} value={k}>{k}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Live Preview Card */}
                                        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginTop: 14 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 10 }}>
                                                👁️ Preview
                                            </div>
                                            <div style={{
                                                border: `1px solid ${C.border}`, borderRadius: 10,
                                                padding: 12, background: '#fff',
                                                display: 'flex', gap: 12, alignItems: 'flex-start'
                                            }}>
                                                {fieldMapping.thumbnailUrl && testResults[0]?.[fieldMapping.thumbnailUrl] ? (
                                                    <img
                                                        src={testResults[0][fieldMapping.thumbnailUrl]}
                                                        alt="thumb"
                                                        style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                                                        onError={(e) => e.target.style.display = 'none'}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: 72, height: 72, borderRadius: 8, flexShrink: 0,
                                                        background: '#F3F4F6', border: '1px solid #E5E7EB',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: 22
                                                    }}>🖼️</div>
                                                )}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13, color: '#111', marginBottom: 5, lineHeight: 1.4 }}>
                                                        {fieldMapping.title && testResults[0]?.[fieldMapping.title]
                                                            ? testResults[0][fieldMapping.title]
                                                            : <span style={{ color: '#D1D5DB' }}>Title not mapped</span>}
                                                    </div>
                                                    <div style={{
                                                        fontSize: 12, color: '#6B7280', marginBottom: 8, lineHeight: 1.5,
                                                        overflow: 'hidden', display: '-webkit-box',
                                                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                                                    }}>
                                                        {fieldMapping.postedDate && testResults[0]?.[fieldMapping.postedDate]
                                                            ? testResults[0][fieldMapping.postedDate]
                                                            : <span style={{ color: '#E5E7EB' }}>Posted Date Not Mapped</span>}
                                                    </div>
                                                    <div style={{
                                                        fontSize: 12, color: '#6B7280', marginBottom: 8, lineHeight: 1.5,
                                                        overflow: 'hidden', display: '-webkit-box',
                                                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                                                    }}>
                                                        {fieldMapping.description && testResults[0]?.[fieldMapping.description]
                                                            ? testResults[0][fieldMapping.description]
                                                            : <span style={{ color: '#E5E7EB' }}>Description not mapped</span>}
                                                    </div>
                                                    {fieldMapping.redirectLink && testResults[0]?.[fieldMapping.redirectLink] && (
                                                        <a
                                                            href={testResults[0][fieldMapping.redirectLink]}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{ fontSize: 12, color: '#6C3FC5', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                                        >
                                                            🔗 Read more
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={!form.name || !form.feedLink || saving}>
                            {saving ? 'Saving…' : modal === 'add' ? 'Create' : 'Update'}
                        </Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}